import React, { useContext, useEffect, useRef, useState } from 'react';
import { NavInlineContext } from './navLayoutContext';

// ---------------------------------------------------------------------------
// NavDropdown
// ---------------------------------------------------------------------------
// A single top-level item in the TopNavBar mega-menu. Click-to-open (not
// hover-to-open): matches the OPERA Cloud reference screenshot's behavior,
// and is far simpler and more robust to implement correctly with plain
// React state than a hover-driven CSS-only menu (no timing/mouse-leave
// races, works identically for keyboard/touch, trivial to add
// click-outside and Escape handling to).
//
// Usage (Phase 2 will map its existing flat nav array into this shape):
//
//   <NavDropdown
//     label="Front Desk"
//     icon={BedDouble}
//     items={[
//       { label: 'Bookings', icon: CalendarCheck, onClick: () => navigate('/bookings') },
//       { label: 'Room Management', icon: DoorOpen, onClick: () => navigate('/rooms') },
//     ]}
//   />
//
// Props:
//   - label (string, required): top-level category label, e.g. "Front Desk".
//   - icon (component, optional): lucide-react icon component reference
//     shown next to the top-level label (NOT a string/element - a
//     component reference, rendered internally as `<Icon />`).
//   - items (array, required): child menu entries, each shaped as:
//       { label: string, icon?: component, onClick?: () => void, onContextMenu?: (event) => void }
//     `icon` here is the same "component reference" convention as the
//     top-level `icon` prop. `onClick` is optional so a purely
//     informational/disabled-looking row can omit it. `onContextMenu`
//     (optional, Phase 2 addition) is passed through untouched to the
//     underlying <button>'s onContextMenu - e.g. for a "right-click to
//     open in a new tab" affordance - and does not affect open/close
//     state on its own (the caller decides whether to preventDefault).
//   - align ('left' | 'right', optional, default 'left'): which edge of
//     the trigger the dropdown panel hangs from. Useful for the
//     right-most items in the bar so the panel doesn't overflow the
//     viewport.
function NavDropdown({ label, icon: Icon, items = [], align = 'left' }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  // Inside TopNavBar's mobile menu: open in the flow, as an accordion.
  const inline = useContext(NavInlineContext);

  // Close on click outside.
  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        className={`${inline ? 'w-full ' : ''}flex items-center gap-1.5 ${Icon ? 'px-3' : 'px-2.5'} py-2 rounded text-sm font-medium text-shell-text hover:bg-shell-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-shell transition-colors`}
      >
        {Icon && <Icon className="w-4 h-4" aria-hidden="true" />}
        <span>{label}</span>
        {inline && <span className="ml-auto text-xs opacity-70" aria-hidden="true">{open ? '▲' : '▼'}</span>}
      </button>

      {open && (
        <div
          role="menu"
          className={inline
            ? 'mt-0.5 mb-1 ml-3 border-l border-white/25 py-0.5'
            : `absolute z-50 mt-1 min-w-[14rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg ${
                align === 'right' ? 'right-0' : 'left-0'
              }`}
        >
          {items.map((item, index) => {
            const ItemIcon = item.icon;
            return (
              <button
                key={item.label ?? index}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onClick && item.onClick();
                }}
                onContextMenu={item.onContextMenu}
                className={inline
                  ? 'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-shell-text rounded hover:bg-shell-dark focus:outline-none focus-visible:bg-shell-dark'
                  : 'flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-shell-light hover:text-shell-dark focus:outline-none focus-visible:bg-shell-light'}
              >
                {ItemIcon && <ItemIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />}
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NavDropdown;
